/**
 * Portfolio Summary Card Component
 * Displays key portfolio metrics and summary information
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PortfolioSummary, PortfolioConfig } from '../types/portfolio';
import { Card } from './Card';

interface PortfolioSummaryCardProps {
  summary: PortfolioSummary;
  config: PortfolioConfig;
}

export const PortfolioSummaryCard: React.FC<PortfolioSummaryCardProps> = ({ 
  summary, 
  config 
}) => {
  const { t } = useTranslation();
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config.baseCurrency,
      minimumFractionDigits: config.displayPrecision,
      maximumFractionDigits: config.displayPrecision,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getChangeColor = (value: number) => {
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getChangeIcon = (value: number) => {
    if (value > 0) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
        </svg>
      );
    }
    if (value < 0) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-9.2 9.2M7 7v10h10" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
      </svg>
    );
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('portfolio.title')}
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Total Value */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4"
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('portfolio.totalValue')}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(summary.totalValue)}
          </div>
        </motion.div>

        {/* Total P&L */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4"
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('portfolio.totalPnL')}
          </div>
          <div className={`text-2xl font-bold ${getChangeColor(summary.totalPnL)}`}>
            {formatCurrency(summary.totalPnL)}
          </div>
          {config.showPercentages && (
            <div className={`text-sm mt-1 ${getChangeColor(summary.totalPnL)} flex items-center gap-1`}>
              {getChangeIcon(summary.totalPnL)}
              {formatPercentage(summary.totalPnLPercent)}
            </div>
          )}
        </motion.div>

        {/* Day Change */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4"
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('portfolio.dayChange')}
          </div>
          <div className={`text-2xl font-bold ${getChangeColor(summary.dayChange)}`}>
            {formatCurrency(summary.dayChange)}
          </div>
          {config.showPercentages && (
            <div className={`text-sm mt-1 ${getChangeColor(summary.dayChange)} flex items-center gap-1`}>
              {getChangeIcon(summary.dayChange)}
              {formatPercentage(summary.dayChangePercent)}
            </div>
          )}
        </motion.div>

        {/* Total Cost */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4"
        >
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('portfolio.totalCost')}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(summary.totalCost)}
          </div>
        </motion.div>
      </div>

      {/* Progress Bar for P&L */}
      {config.showUnrealizedPnL && summary.totalCost > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6"
        >
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
            <span>{t('portfolio.performance')}</span>
            <span>{formatPercentage(summary.totalPnLPercent)}</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                summary.totalPnL >= 0 
                  ? 'bg-green-500' 
                  : 'bg-red-500'
              }`}
              style={{
                width: `${Math.min(Math.abs(summary.totalPnLPercent), 100)}%`,
              }}
            />
          </div>
        </motion.div>
      )}
    </Card>
  );
};